import { FilmScreening } from "@/app/[locale]/(rest)/films/[slug]/components/ScreeningList";
import { AddToCartFilm } from "@/components/ui/AddToCartButton";
import { Enums, Tables } from "@/types/database.types";
import { create } from "zustand";
import { persist } from "zustand/middleware";

type ScreeningType = Enums<"screening_type">;
type PurchaseLocation = Enums<"purchase_location_enum">;
export type ShortProgramme = Tables<"short_programme">;

export function generateOrderNumber() {
    const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const randomPart = Math.floor(1000 + Math.random() * 9000);
    return `IN-${datePart}-${randomPart}`;
}

export type Ticket =
    & Omit<
        Tables<"tickets">,
        | "purchase_location"
        | "usage_limits"
        | "short_programme"
        | "name"
        | "description"
    >
    & {
        name: {
            en: string;
            fr: string;
        };
        description: {
            en: string;
            fr: string;
        };
        purchase_location: PurchaseLocation;
        usage_limits: {
            single_use: boolean;
            online: number;
            in_cinema: number;
        };
    };

export interface CartItem {
    ticket: Ticket;
    film?: AddToCartFilm;
    screening?: FilmScreening;
    quantity: number;
}

interface CartState {
    orderNumber: string;
    items: CartItem[];
    addItem: (item: CartItem) => void;
    removeItem: (ticketId: string, screeningId?: string) => void;
    clearCart: () => void;
    increaseQuantity: (ticketId: string, screeningId?: string) => void;
    decreaseQuantity: (ticketId: string, screeningId?: string) => void;
    clearExpiredState: () => void;
}

const resetExpirationTime = () => {
    const currentTime = new Date().getTime();
    const newExpirationTime = currentTime + 24 * 60 * 60 * 1000;
    localStorage.setItem("cart_expiration", newExpirationTime.toString());
};

const useCartStore = create<CartState>()(
    persist(
        (set) => ({
            orderNumber: generateOrderNumber(),
            items: [],

            addItem: (item: CartItem) => {
                resetExpirationTime();
                set((state) => {
                    const existingItemIndex = state.items.findIndex((
                        cartItem,
                    ) => cartItem.screening
                        ? cartItem.ticket.id === item.ticket.id &&
                            cartItem.screening.id === item.screening?.id
                        : cartItem.ticket.id === item.ticket.id
                    );

                    if (existingItemIndex !== -1) {
                        if (item.screening?.screening_type === "ONLINE") {
                            return { items: state.items };
                        }

                        const updatedItems = [...state.items];
                        updatedItems[existingItemIndex].quantity +=
                            item.quantity;
                        return { items: updatedItems };
                    }

                    return { items: [...state.items, item] };
                });
            },

            removeItem: (ticketId: string, screeningId?: string) => {
                resetExpirationTime();
                set((state) => ({
                    items: state.items.filter((item) =>
                        screeningId
                            ? item.ticket.id !== ticketId ||
                                item.screening?.id !== screeningId
                            : item.ticket.id !== ticketId
                    ),
                }));
            },

            clearCart: () => {
                localStorage.removeItem("cart_expiration");
                set({ items: [], orderNumber: generateOrderNumber() });
            },

            increaseQuantity: (ticketId: string, screeningId?: string) => {
                resetExpirationTime();
                set((state) => {
                    const updatedItems = state.items.map((item) => {
                        if (
                            item.ticket.id === ticketId &&
                            item.screening?.id === screeningId
                        ) {
                            return { ...item, quantity: item.quantity + 1 };
                        }
                        return item;
                    });
                    return { items: updatedItems };
                });
            },

            decreaseQuantity: (ticketId: string, screeningId?: string) => {
                resetExpirationTime();
                set((state) => {
                    const updatedItems = state.items
                        .map((item) => {
                            if (
                                item.ticket.id === ticketId &&
                                item.screening?.id === screeningId
                            ) {
                                return { ...item, quantity: item.quantity - 1 };
                            }
                            return item;
                        })
                        .filter((item) => item.quantity > 0);
                    return { items: updatedItems };
                });
            },

            clearExpiredState: () => {
                const currentTime = new Date().getTime();
                const expirationTime = localStorage.getItem("cart_expiration");

                if (
                    expirationTime && currentTime > parseInt(expirationTime, 10)
                ) {
                    set({ items: [], orderNumber: generateOrderNumber() });
                    localStorage.removeItem("cart_expiration");
                }
            },
        }),
        {
            name: "cart-storage",
            onRehydrateStorage: () => (state) => {
                const currentTime = new Date().getTime();
                const expirationTime = localStorage.getItem("cart_expiration");

                if (!expirationTime) {
                    const newExpirationTime = currentTime + 24 * 60 * 60 * 1000;
                    localStorage.setItem(
                        "cart_expiration",
                        newExpirationTime.toString(),
                    );
                } else if (currentTime > parseInt(expirationTime, 10)) {
                    state?.clearCart();
                    localStorage.removeItem("cart_expiration");
                }
            },
        },
    ),
);

export default useCartStore;
